/**
 * Contact Support Service
 *
 * Domain layer service for contact / support-request submissions.
 *
 * Singleton pattern for consistent state and efficient resource usage.
 *
 * Architecture:
 * UseCase -> ContactSupportService -> CloudFunctionsService
 *
 * Flutter has no Clean Architecture layering for this flow:
 * `lib/presentation/web/contact_screen.dart:226-231` calls
 * `CloudFunctionsService.submitSupportRequest(...)` directly. The web port
 * adds this service + a use-case wrapper per project convention (mirrors
 * `domain/services/report/reportService.ts`).
 */

import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';

export class ContactSupportService {
  private static instance: ContactSupportService;

  private constructor() {}

  public static getInstance(): ContactSupportService {
    if (!ContactSupportService.instance) {
      ContactSupportService.instance = new ContactSupportService();
    }
    return ContactSupportService.instance;
  }

  /**
   * Submit a support request via the cloud function.
   * Matches Flutter's `CloudFunctionsService.submitSupportRequest`
   * (`lib/services/cloud_functions_service.dart:806-831`).
   *
   * The data layer already catches errors and returns `false`, so this
   * service is a thin pass-through — no remapping needed (return type is
   * a primitive boolean).
   *
   * @param content - Optional free-text request body (empty string is
   *                  forwarded unchanged, matching Flutter's behavior).
   * @param emailAddress - The user-supplied contact email.
   * @returns `true` when the cloud function reports success, `false`
   *          otherwise (including any network / parse error).
   */
  async submitSupportRequest(content: string, emailAddress: string): Promise<boolean> {
    return cloudFunctionsService.submitSupportRequest(content, emailAddress);
  }
}

export const contactSupportService = ContactSupportService.getInstance();
